<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { Loader2, Mountain, TrendingUp, TrendingDown } from 'lucide-vue-next'
import { bbox, center, length, distance } from '@turf/turf'
import { MAPBOX_ACCESS_TOKEN, MAPBOX_API } from '@/config/mapbox'

// Constants
const MAX_ELEVATION_POINTS = 100
const CHART_RENDER_DELAY = 100 // Delay for chart rendering to ensure DOM is ready

const props = defineProps({
  geometry: {
    type: Object,
    required: true
  }
})

const elevationData = ref(null)
const loading = ref(false)
const error = ref(null)
const chartContainer = ref(null)


// Compute statistics from elevation data
const elevationStats = computed(() => {
  if (!elevationData.value || !elevationData.value.length) return null

  const elevations = elevationData.value.map(d => d.elevation)
  const min = Math.min(...elevations)
  const max = Math.max(...elevations)
  const avg = elevations.reduce((a, b) => a + b, 0) / elevations.length

  // Calculate total distance for LineString
  let totalDistance = 0
  if (props.geometry.type === 'LineString' && elevationData.value.length > 1) {
    for (let i = 1; i < elevationData.value.length; i++) {
      totalDistance += elevationData.value[i].distance - elevationData.value[i - 1].distance
    }
  }

  return {
    min: min.toFixed(1),
    max: max.toFixed(1),
    avg: avg.toFixed(1),
    range: (max - min).toFixed(1),
    totalDistance: totalDistance.toFixed(2)
  }
})

// Sample points along geometry for elevation queries
const samplePoints = computed(() => {
  const points = []

  if (props.geometry.type === 'Point') {
    points.push(props.geometry.coordinates)
  } else if (props.geometry.type === 'LineString') {
    // Sample up to MAX_ELEVATION_POINTS points along the line
    const coords = props.geometry.coordinates
    const step = Math.max(1, Math.floor(coords.length / MAX_ELEVATION_POINTS))
    for (let i = 0; i < coords.length; i += step) {
      points.push(coords[i])
    }
    // Always include the last point
    if (
      coords.length > 0 &&
      JSON.stringify(points[points.length - 1]) !== JSON.stringify(coords[coords.length - 1])
    ) {
      points.push(coords[coords.length - 1])
    }
  } else if (props.geometry.type === 'Polygon') {
    // Get center point and corners of polygon
    const centerPoint = center(props.geometry)
    if (centerPoint) {
      points.push(centerPoint.geometry.coordinates)
    }
    // Sample some points from the boundary
    const boundary = props.geometry.coordinates[0]
    const step = Math.max(1, Math.floor(boundary.length / 20))
    for (let i = 0; i < boundary.length - 1; i += step) {
      points.push(boundary[i])
    }
  }

  return points.slice(0, MAX_ELEVATION_POINTS)
})

// Fetch elevation data from Mapbox Terrain RGB tiles
const fetchElevationData = async () => {
  if (!samplePoints.value.length) {
    error.value = 'No points available for elevation query'
    return
  }

  loading.value = true
  error.value = null

  try {
    const elevations = []
    let cumulativeDistance = 0
    let prevPoint = null

    for (const point of samplePoints.value) {
      const [lng, lat] = point

      // Calculate distance from previous point (for LineString)
      if (prevPoint && props.geometry.type === 'LineString') {
        // Use turf.distance for maintainability
        const prevFeature = { type: 'Feature', geometry: { type: 'Point', coordinates: prevPoint } }
        const currFeature = { type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] } }
        const dist = distance(prevFeature, currFeature, { units: 'meters' })
        cumulativeDistance += dist
      }

      // Query elevation using Mapbox Tilequery API
      const url = `${MAPBOX_API.TILEQUERY}/${lng},${lat}.json?layers=contour&access_token=${MAPBOX_ACCESS_TOKEN}`

      try {
        const response = await fetch(url)
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
        const data = await response.json()

        // Extract elevation from contour features
        let elevation = 0
        if (data.features && data.features.length > 0) {
          // Find the highest resolution contour
          const contourFeature = data.features.find(f => f.properties.ele !== undefined)
          if (contourFeature) {
            elevation = contourFeature.properties.ele
          }
        }

        elevations.push({
          coordinates: [lng, lat],
          elevation: elevation,
          distance: cumulativeDistance
        })

        prevPoint = point
      } catch (err) {
        console.error('Error fetching elevation for point:', point, err)
        // Continue with next point
      }
    }

    elevationData.value = elevations
  } catch (err) {
    error.value = `Failed to fetch elevation data: ${err.message}`
    console.error('Elevation fetch error:', err)
  } finally {
    loading.value = false
  }
}

// Draw elevation profile chart for LineString
const drawElevationProfile = () => {
  if (!elevationData.value || !chartContainer.value || props.geometry.type !== 'LineString') return

  const container = chartContainer.value
  const width = container.clientWidth
  const height = 200
  const padding = { top: 20, right: 20, bottom: 40, left: 50 }

  // Clear any existing chart
  container.innerHTML = ''

  // Create SVG
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('width', width)
  svg.setAttribute('height', height)
  svg.setAttribute('class', 'elevation-chart')

  // Calculate scales
  const distances = elevationData.value.map(d => d.distance)
  const elevations = elevationData.value.map(d => d.elevation)
  const xScale = (d) => padding.left + (d / Math.max(...distances)) * (width - padding.left - padding.right)
  const yScale = (e) => height - padding.bottom - ((e - Math.min(...elevations)) / (Math.max(...elevations) - Math.min(...elevations))) * (height - padding.top - padding.bottom)

  // Create path
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  const pathData = elevationData.value
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(d.distance)} ${yScale(d.elevation)}`)
    .join(' ')
  path.setAttribute('d', pathData)
  path.setAttribute('fill', 'none')
  path.setAttribute('stroke', '#3b82f6')
  path.setAttribute('stroke-width', '2')

  // Create area fill
  const area = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  const areaData = pathData + ` L ${xScale(Math.max(...distances))} ${height - padding.bottom} L ${xScale(0)} ${height - padding.bottom} Z`
  area.setAttribute('d', areaData)
  area.setAttribute('fill', '#3b82f6')
  area.setAttribute('fill-opacity', '0.1')

  // Add grid lines
  const gridGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
  gridGroup.setAttribute('class', 'grid')

  // Horizontal grid lines
  const elevationSteps = 5
  const elevationRange = Math.max(...elevations) - Math.min(...elevations)
  for (let i = 0; i <= elevationSteps; i++) {
    const elevation = Math.min(...elevations) + (elevationRange * i / elevationSteps)
    const y = yScale(elevation)

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    line.setAttribute('x1', padding.left)
    line.setAttribute('y1', y)
    line.setAttribute('x2', width - padding.right)
    line.setAttribute('y2', y)
    line.setAttribute('stroke', '#e5e7eb')
    line.setAttribute('stroke-width', '1')
    gridGroup.appendChild(line)

    // Add label
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    text.setAttribute('x', padding.left - 5)
    text.setAttribute('y', y + 4)
    text.setAttribute('text-anchor', 'end')
    text.setAttribute('font-size', '12')
    text.setAttribute('fill', '#6b7280')
    text.textContent = `${elevation.toFixed(0)}m`
    gridGroup.appendChild(text)
  }

  // Add elements to SVG
  svg.appendChild(gridGroup)
  svg.appendChild(area)
  svg.appendChild(path)

  // Add axis labels
  const xLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text')
  xLabel.setAttribute('x', width / 2)
  xLabel.setAttribute('y', height - 5)
  xLabel.setAttribute('text-anchor', 'middle')
  xLabel.setAttribute('font-size', '12')
  xLabel.setAttribute('fill', '#6b7280')
  xLabel.textContent = 'Distance (m)'
  svg.appendChild(xLabel)

  container.appendChild(svg)
}

// Watch for geometry changes
watch(() => props.geometry, () => {
  fetchElevationData()
}, { deep: true })

// Watch for elevation data changes to redraw chart
watch(elevationData, () => {
  if (elevationData.value && props.geometry.type === 'LineString') {
    setTimeout(drawElevationProfile, CHART_RENDER_DELAY)
  }
})

// Handle window resize for chart
const resizeHandler = () => {
  if (elevationData.value && props.geometry.type === 'LineString') {
    drawElevationProfile()
  }
}

// Initial setup
onMounted(() => {
  fetchElevationData()
  window.addEventListener('resize', resizeHandler)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeHandler)
})
</script>

<template>
  <div class="py-2">
    <div v-if="loading" class="flex items-center justify-center p-4">
      <Loader2 class="w-5 h-5 animate-spin text-gray-500" />
      <span class="ml-2 text-gray-500">Loading elevation data...</span>
    </div>

    <div v-else-if="error" class="text-red-500 text-sm p-2">
      {{ error }}
    </div>

    <div v-else-if="elevationStats">
      <!-- Single point elevation -->
      <div v-if="geometry.type === 'Point'" class="flex items-center space-x-2">
        <Mountain class="w-4 h-4 text-gray-500" />
        <span class="text-gray-700">
          Elevation: <strong>{{ elevationStats.avg }}m</strong>
        </span>
      </div>

      <!-- Line or polygon elevation stats -->
      <div v-else>
        <div class="grid grid-cols-2 gap-2 mb-3 text-sm">
          <div class="flex items-center space-x-1">
            <TrendingDown class="w-4 h-4 text-blue-500" />
            <span class="text-gray-600">Min:</span>
            <strong class="text-gray-800">{{ elevationStats.min }}m</strong>
          </div>
          <div class="flex items-center space-x-1">
            <TrendingUp class="w-4 h-4 text-red-500" />
            <span class="text-gray-600">Max:</span>
            <strong class="text-gray-800">{{ elevationStats.max }}m</strong>
          </div>
          <div class="flex items-center space-x-1">
            <Mountain class="w-4 h-4 text-gray-500" />
            <span class="text-gray-600">Avg:</span>
            <strong class="text-gray-800">{{ elevationStats.avg }}m</strong>
          </div>
          <div class="flex items-center space-x-1">
            <span class="text-gray-600">Range:</span>
            <strong class="text-gray-800">{{ elevationStats.range }}m</strong>
          </div>
          <div v-if="geometry.type === 'LineString' && elevationStats.totalDistance" class="col-span-2 flex items-center space-x-1">
            <span class="text-gray-600">Distance:</span>
            <strong class="text-gray-800">{{ elevationStats.totalDistance }}m</strong>
          </div>
        </div>

        <!-- Elevation profile chart for LineString -->
        <div v-if="geometry.type === 'LineString'" ref="chartContainer" class="w-full min-h-[200px] mt-3 border rounded-lg p-2 bg-gray-50"></div>
      </div>
    </div>

    <div v-else class="text-gray-500 text-sm">
      No elevation data available
    </div>
  </div>
</template>